import { DateTime } from "luxon";
import { Moment } from "moment";

/** Service offering details for use with BookingApiV2 */
export interface BookingServiceOffering {
    TaxiServiceId: number;
    NumberOfSeats: number | null;
    CityId: number;
}

/** A low-level data type roughly corresponding to a type of vehicle.
 *  Corresponds to the Bcc.Api.Model.ViewModel.Condition class in the Booking API. */
export interface Condition {

    /** Internal ID, from the database */
    ID: number;

    /** "Sedan" or "Wagon" etc */
    Name: string;

    /** Number of passengers this type of vehicle seats. */
    MaxSeat: number;

    /** "up to 7 passengers" */
    Description: string;
}

/** Data contract for Bookings loaded from the server. */
export interface BookingTrackingResult {

    /** 
     *  The recent history of the vehicle's GPS position.
     *  The array elements are in ascending time order. i.e. the last entry is the most current.
     *  Sorry about the typo; it's stuck in the API contract.
     *  WARNING: only populated by the TrackBookingByKey API (after boooking creation).
     */
    vechileLocation?: VehicleLocation[];

    /** Taxi number, e.g. T1234 */
    CarNumber?: string;

    /** Requested pickup time. DateTime formatted as string */
    Time: string;

    /** 
     * The requested pickup time, formatted using ISO 8601, including a UTC offset corresponding to the local time zone of the booking. 
     * It may rarely be null when the service could not determine the local time zone.
     */
    LocalTime: string | null;

    DriverName: string | null;

    /** URL to a photo of the driver */
    DriverImage: string | null;

    /** ID of the driver assigned to the booking. 
     * This is not in the initial tracking response and it is populated later with get status call. Therefore it is optional. */
    DriverID?: number | null;

    /** A string in like "1 - 4" for a 4-passenger vehicle */
    MaxSeat: string;

    CarConditionID?: number;

    /** Service offering details. Only populated for BookingApiV2 bookings. */
    ServiceOffering?: BookingServiceOffering;

    /** ID corresponding to the booking status, e.g. NotDispatched = 1, etc. */
    BookingStatusID: BookingStatus;

    /** Internal booking ID */
    DispatchBookingID: number;

    /** Main booking ID */
    BookingID: number;

    /** Internal hash of the booking details. This value is private to the owner of the booking and is used to prove ownership in the guest scenario. */
    HashCode: string;

    /** Passenger name. This can be different from the user name of the profile. */
    PassengerName: string;

    /** Who owns the booking. This influences what actions are available for the user to perform.
     * Populated by the Management service. */
    DataOwnership?: BookingDataOwnership;

    /** Contact name */
    ContactName: string;

    /** 
     * Driver remarks
     * WARNING: only populated by the TrackBookingByKey API (after boooking creation). 
     **/
    NotesToDriver?: string;

    /** Order number */
    OrderNumber?: string;

    /** Account number */
    AccountNumber?: string;

    /** Passenger/Contact phone number */
    CustomerPhone?: string;

    /** Access code used in the tracking URL in share booking. The users with this code has read only access to the particular booking. */
    ReadAccessCode?: string;

    /** Base URL of the booking tracking link. This is combined with the ReadAccessCode to make the share booking link. */
    BookingTrackingBaseUrl?: string;

    /** Google Maps Trip IDs for all bookings. Any Google API calls used during tracking of the bookings should pass in the Trip ID if it is non-null. */
    GoogleOdrdTripId?: string;

    /** Whether this booking is a booking made via the Delivery API. */
    IsDeliveryBooking: boolean;

    /**
     * True when the booking was cancelled by the server due to a JIT Preauth failure
     */
    WasJitPreauthFailure: boolean;

    /**
     * Jit PreAuth failure user friendly message to be displayed in the UI.
     */
    JitPreauthFailureMessage: string | null;

    /** List of locations of the booking with address specific details such as coordinates and address text. Other location based data like contact details and driver notes will be retrieved for a single booking when needed. */
    Locations: BookingLocationDetails[];
}

/** Who owns a booking */
export enum BookingDataOwnership {

    /** Created as a guest booking. Not associated with any user. */
    Guest = "Guest",

    /** Created while signed in. Owned by the current user. */
    User = "User",

    /** Test data load. Not owned by the current user. */
    Test = "Test",

    /** This booking has been opened via a shared link with read only access. */
    ReadOnlyLink = "ReadOnlyLink",

    /** A tracking link with write access. The user can cancel this booking, and may attempt to promote it. */
    WritableLink = "WriteableLink",
}

export interface BookingLocationResult {
    
    /** Driver notes */
    remark: string,

    /** Placeid for Pickup/Dropoff address. Can be null if the booking was created in a different channel. */
    googlePlaceId: string | null,

    isPickup: boolean,

    /** Booking Pickup/Dropoff contact details*/
    contactDetails: BookingLocationContactDetail
}

/** NotesToDriver Response from Booking Mgmt */
export interface NotesToDriverResult {
    Message: string
}

/** Booking Statuses from the API. Possibly don't need the bottom half */
export enum BookingStatus {
    /** In the future and not yet trying to find a cab */
    NotDispatched = 1,

    /** Looking for a cab to take this job */
    Dispatching = 2,

    /** Job has been offered to a cab */
    Assigned = 3,

    /** Cab has received offer */
    Acknowledged = 4,

    /** Cab has accepted the job, headed to pickup */
    Accepted = 5,

    /** Meter on */
    PickedUp = 6,

    /** Meter off */
    Completed = 7,

    /** Cancelled by user */
    Cancelled = 8,

    /** Customer could not be found at pickup point */
    NoJob = 9,

    // There's a bunch more statuses we don't handle
}

/** GPS coordinates. Careful: it differs from the other GeoPoint type we use internally, which has lower case L's. */
export interface ApiGeoPoint {
    Latitude: number;
    Longitude: number;
}

/** The location of a Vehicle at some point in time */
export interface VehicleLocation extends ApiGeoPoint {

    /** Unfortunately the format of this is not well known. It comes out as a string from several API layers deep */
    Time: string;

    /** When the data was loaded on the client. Only populated at client side, from BulkStatusRefresh. */
    LocalDataLoadedTime?: Moment;
}

/** Minimal version of a Booking status, optimised for vehicle tracking polling.
 * It only contains the fields that can change over time, like status, car number, driver. */
export interface LightweightBookingStatus {

    /** The Booking ID, to help identify which record is which when you request multiple booking records in one query. */
    BookingId: number;

    /** Well-known ID for booking status */
    BookingStatusId: BookingStatus;

    /** Car number suitable for display to human.
     * May be null if there is no car defined, e.g. booking is pre-dispatching.*/
    CarNumberPlate: string | null;

    /** Internal Booking API Driver ID for the driver, or null if the Booking does not have a driver associated with it yet. */
    DriverId: number | null;

    /** GPS location of the car, if known. Only populated when the booking is Accepted or PickedUp. */
    VehicleLocation: VehicleLocation | null;

    /**
     * True when the booking was cancelled by the server due to a JIT Preauth failure.
     */
    WasJitPreauthFailure: boolean;

    /**
     * Jit PreAuth failure user friendly message to be displayed in the UI.
     */
    JitPreauthFailureMessage: string;
}

/** A Booking ID combined with the hashcode proves the client's ownership of the booking. */
export interface BookingAuthority {
    bookingId: number;
    hashCode: string;
}

/** Request to get the list of recent bookings of the logged in user. */
export interface MyBookingsRequest {
    /** A list of booking statuses. Useful to get active bookings or history based on user selection. */
    StatusIDs: number[];

    /** Number of bookings to be retrieved. */
    Limit: number;

    /** How many bookings to be skipped (offset). Useful for pagination. */
    Skip: number;
}

/** Request to get the list of bookings by date range of the logged in user. */
export interface ApiDateRange {

    /** ISO 8601 format. Inclusive. */
    StartDateInclusive: string;

    /** ISO 8601 format. Exclusive: bookings on this day are NOT part of the result set. */
    EndDateExclusive: string;
}

/** Request to share a booking via SMS */
export interface ShareBookingRequest {
    /** BookingID */
    BookingID: number;

    /** Mobile number to send the SMS */
    PhoneNumber: string;

    /** Dispatch booking ID */
    DispatchBookingID: string;

    /** Booking Hashcode (this is read only hashcode) */
    HashCode: string;

    /** Booking date and time */
    BookingDateTime: string;

    /** API version of the booking. This is used in the backend for validation and to determine the tracking link. */
    ApiVersion: number;
}

/** Read only hashcode and the share URL for share booking */
export interface ReadOnlyHash {
    /** Read only hashcode */
    HashCode: string;

    /** Corresponding mobile website's URL to display the share URL */
    UrlBase: string;
}

/** Minimal details of the driver assigned to a particular booking. */
export interface BookingDriverDetails {
    BookingId: number;

    /** Display name of the driver. */
    DriverName: string | null;

    /** URL of the location where the driver's image is stored. */
    DriverImageURL: string | null;
}

/** Create booking request payload. */
export interface BookingRequestV1b {

    /** Booking's main contact details. */
    ContactInfo: BookingContactDetails;

    /** Payment details including payment method, fare etc */
    Payment: BookingPaymentV1;

    /** Number of passengers travelling in this booking. */
    Pax: number;

    /** Timing information */
    Timing: BookingTimingV1;

    /** User selected conditions. */
    Conditions: ConditionV1[] | null;

    /** The ID of an already verified SMS challenge, which proves ownership of the [contactPhone] phone number. 
     * This is required for guest bookings. */
    SmsChallengeId: string | null;

    /** Meta information about the client. */
    Client: ClientDetails;

    /** End-of-delivery options for a parcel delivery job created using the Parcel Widget. 
     *  Will be null if this isn't a parcel delivery. */
    ParcelOptions: ParcelDeliveryOptions | null;

    /**
     * The Google ODRD Trip ID that the client has been using for any Google Maps API calls leading up to the booking's creation.
     * This opts the booking into the ODRD per-trip billing mechanism.
     * Format: a GUID. Required.
     */
    GoogleOdrdTripId: string;

    /**
     * Features (new) the website is capable of handling (e.g.: JIT preauth). This prop is set to avoid backwards compatibility breakages.
     */
    ClientCapabilities: BookingClientCapabilities;

    /** The list of locations for the booking including contact details and driver notes per location. It can have up to five locations. */
    Locations: BookingLocationV1[];
}

/** Booking location details. */
export interface BookingLocationV1 {

    /** Google Maps place ID for the location. */
    PlaceId: string;

    /** Contact person and details for this location. i.e.: pickup and dropoff locations can have different contacts */
    ContactDetails: BookingContactDetails;

    /** Optional instructions to the driver. */
    DriverInstructions: string | null;

    /** Optional geolocation of the location. In most cases we use only PlaceId, but in case of curated locations, Geo Location is more accurate than PlaceId. */
    GeoLocation: ApiGeoPoint | null;
}

/** Contact details of various parties involved in a booking. e.g.: main contact person (booker), pickup contact, dropoff contact */
export interface BookingContactDetails {
    Name: string;

    /** E.164 format including leading "+" and country dial code prefix. */
    PhoneNumber: string;
}

/** Payment related data associated with a booking. */
export interface BookingPaymentV1 {

    /** User's device information(used for fraud detection), it is set using braintree library, required to successfully do an app payment.
     * Booking api decides whether it is valid card booking by checking existence of device data field.
     * MPS also sends this devicedata to braintree along with transaction request,braintree uses kount for fraud detection.
     */
    DeviceData: string | null;

    /** Selected payment card's ID (not the card number). */
    PaymentCardId: string | null;

    /** Fare details. Null only if fixed fare details are not available for the booking location. */
    Fare: FareEstimate | null;

    /** Account details if the booking is paid with an account. */
    AccountInfo: BookingAccountV1 | null;

    /** Whether this booking/payment should be pre-authorised. Should be true for all CNP bookings if feature flag is enabled. */
    PaymentPreAuthOptIn: boolean;

    /** Whether the client chose this booking to be fixed fare.
     * This is required in addition to FixedFareId and FixedFarePartitionKey to specify a booking is actually fixed fare since other fields are also populated for meter fare bookings for pre auth purpose.*/
    IsFixedFare: boolean;

    /** A nonce obtained from 3DS verification of the previously-vaulted payment card used with the booking. */
    Upgraded3DsNonce: string | null;

    /** Liability for fraudulent chargebacks has been shifted from the merchant (A2B) to the card issuer during the 3DS authentication. Can be null if 3DS wasn't used. */
    LiabilityShifted3Ds: boolean | null;

    /** Email address that a receipt should be sent to for a CNP booking when a trip is completed.
     * This is only populated for guest users (or logged in users in future may be, if they want to use a different email address for the receipt). */
    ReceiptEmail: string | null;
}

/** When the booking occurs (pickup time details) */
export interface BookingTimingV1 {

    /** String representation of a datetime. This is the requested pickup time.
     * [Obsolete] will be removed after all the clients are released. */
    Time: string | null;

    /**
     * The requested time, expressed in the local timezone of the booking, formatted using ISO 8601 with a time zone offset.
     * This field is optional and replaces (supersedes) [Time] when populated.
     */
    LocalTime: string | null;

    /** Whether the pickup is ASAP. When false (future booking), the pickup time must be populated. */
    IsImmediate: boolean;
}

/** Meta information about the current client. */
export interface ClientDetails {

    /** Whether the client is in mobile mode. */
    IsMobile: boolean;
}

/** Account related details in create booking request. */
export interface BookingAccountV1 {
    AccountNumber: string;

    /** Order number if applicable. */
    OrderNumber: string | null;
}

/** Fare estimate details. Populated for all bookings (fixed fare/meter fare) if available for the booking location. */
export interface FareEstimate {

    /** Unique ID of the selected fixed fare (unique for each vehicle type). */
    FixedFareId: string;

    /** CosmosDB partition key where the selected Fixed fare is stored. */
    FixedFarePartitionKey: string;
}

/** Selected conditions details. */
export interface ConditionV1 {

    /** Condition ID from the BCC database. */
    ID: number;
}

export interface BookingLocationContactDetail {

    /** booking location contact name for pickup/drop-off. */
    name: string,

    /** booking location contact number for pickup/drop-off. */
    phoneNumber: string
}

/** The type of check that should be carried out by a driver at the end of a parcel delivery for an age-restricted item such as alcohol. */
export interface ParcelDeliveryOptions {

    /** The type of check that should be carried out by a driver at the end of a delivery for an age-restricted item such as alcohol. */
    ParcelRestrictedItemCheck: RestrictedItemCheck;

    /** When set to true, the driver will obtain proof of delivery via customer signature or photograph, depending on whether or not this is an unattended delivery. */
    RequireProofOfDelivery: boolean;

    /** When true, the parcel can be left unattended by the driver, with a photo taken as proof of delivery. 
     *  Only available for non-restricted items. */
    AllowUnattendedDelivery: boolean;
}

/** The type of check that should be carried out by a driver at the end of a parcel delivery for an age-restricted item such as alcohol. */
export enum RestrictedItemCheck {

    /** No check required if we are not dealing with an age-restricted item. */
    None = "None",

    /** Basic Proof Of Age (Non-RSA).
     *  Delivery driver must sight ID if the person looks under 25 years of age when this is an item that can only be accepted by an adult, e.g. alcohol. */
    BasicProofOfAge = "BasicProofOfAge",

    /** Responsible Service of Alcohol. A mandatory ID check for a nominated recipient over 18 years of age.
     *  Their name must be the same as the drop-off contact name, and their signature and date of birth may be recorded. 
     *  RSA implies proof of delivery. */
    RSA = "RSA",
}

/** 
 * Client(website) capabilities for new features to avoid backwards compatibility breakages.
 */
export interface BookingClientCapabilities {

    /** 
     * To indicate if the website has the capability and specific UX flow for Just In Time preauth.
     * When this is true and client calls GET booking endpoint, the client must check for the WasJitPreauthFailure flag on a booking, read the field JitPreauthFailureMessage and display a UI indicating payment preauth has failed with remediation options (e.g. rebooking).
     */
    JustInTimePreauth: boolean;
}

/** Address specific details of a booking location. These are the minimum details required by the website to display the location in the bookings UI.*/
export interface BookingLocationDetails {

    /** Lat, long coordinates of the location. May be null if the geolocation data is not available (rare) */
    GeoPoint: ApiGeoPoint | null;

    /** Human readable address text of the location. It can be a street address or a place name. */
    AddressText: string;
}